import React from 'react';
import Button from '@material-ui/core/Button';
import Link from '@material-ui/core/Link';
import GetAppIcon from '@material-ui/icons/GetApp';
import { Link as RouterLink } from 'react-router-dom';
// Components
import { TooltipButton } from '../../components/tooltip-button';
// Ressources
import { getListCompaniesURL, getListPartnersURL, getListRidersURL } from '../../utils/core-api';
import { paths } from '../../routes/paths';
import { FormatType, HeadCell } from '../../local-types';
import DateUtils from '../../utils/dateUtils';
import StringUtils from '../../utils/stringUtils';

export enum Cell {
    ID = 'id',
    NAME = 'name',
    FULLNAME = 'fullname',
    EMAIL = 'email',
    PHONE_NUMBER = 'phone_number',
    BILL_NUMBER = 'bill_number',
    BILL_DOCUMENT_PATH = 'bill_document_path',
    BILL_XLS_PATH = 'bill_xls_path',
    BILL_RELATED_BILL_NUMBER = 'related_bill_number',
    BILL_CREATED_AT = 'bill_created_at',
    USER_ID = 'user_id',
    COMPANY = 'company',
    COMPANY_ID = 'company_id',
    PARTNER_ID = 'partner_id',
    RIDE_NUMBER = 'ride_number',
    LABEL = 'label',
    AMOUNT_PRE_TAX = 'amount_pre_tax',
    AMOUNT_WITH_TAX = 'amount_with_tax',
    STATUS = 'status',
    PERIOD = 'period',
    CREATION_DATE = 'creation_date',
}

export function createCell(t, cell: Cell, args?: Partial<HeadCell>): HeadCell {
    let selected = {} as HeadCell;
    switch (cell) {
        case Cell.ID:
            selected = { id: 'id', label: t('common:form.id'), contains: false, sortable: false };
            break;
        case Cell.NAME:
            selected = { id: 'name', label: t('common:form.lastname') };
            break;
        case Cell.FULLNAME:
            selected = {
                id: 'fullname',
                label: t('common:form.lastname'),
                formatCell: (row) => {
                    return StringUtils.userFullname(row);
                },
            };
            break;
        case Cell.EMAIL:
            selected = {
                id: 'email',
                label: t('common:form.email'),
                formatCell: (row) => row.email || row.emails?.map((e) => e.email).join(', '),
            };
            break;
        case Cell.PHONE_NUMBER:
            selected = { id: 'phone_number', label: t('common:form.phone_number'), type: FormatType.Phone };
            break;
        case Cell.BILL_NUMBER:
            selected = { id: 'bill_number', label: t('common:form.id'), contains: false };
            break;
        case Cell.BILL_DOCUMENT_PATH:
            selected = {
                id: 'document_path',
                label: t('common:pdf'),
                filterable: false,
                sortable: false,
                noLink: true,
                formatCell: function (row) {
                    return (
                        <div style={{ display: 'flex' }}>
                            <TooltipButton title={t('common:download')}>
                                <Button
                                    size="small"
                                    variant="contained"
                                    color="secondary"
                                    style={{ minWidth: 'auto', padding: 4 }}
                                    href={row.document_path}
                                >
                                    <GetAppIcon />
                                </Button>
                            </TooltipButton>
                        </div>
                    );
                },
            };
            break;
        case Cell.BILL_XLS_PATH:
            selected = {
                id: 'export_path',
                label: t('common:xls'),
                filterable: false,
                sortable: false,
                formatCell: function (row) {
                    return (
                        row.export_path && (
                            <TooltipButton title={t('common:download')}>
                                <Button
                                    size="small"
                                    variant="contained"
                                    color="secondary"
                                    style={{ minWidth: 'auto', padding: 4 }}
                                    href={row.export_path}
                                >
                                    <GetAppIcon />
                                </Button>
                            </TooltipButton>
                        )
                    );
                },
            };
            break;
        case Cell.BILL_RELATED_BILL_NUMBER:
            selected = {
                id: 'related_bill_number',
                label: t('bill_id'),
                formatCell: (row) => {
                    const { related_bill_number, type } = row;
                    let page = 'simple';
                    if (type === 'month') {
                        page = 'periods';
                    } else if (type === 'exceptional') {
                        page = 'exceptionals';
                    }
                    return (
                        related_bill_number && (
                            <Link
                                color="secondary"
                                component={RouterLink}
                                to={`${paths.bills}/${page}?search[bill_number]=${related_bill_number}&after[created_at]=2000-01-01`}
                            >
                                {related_bill_number}
                            </Link>
                        )
                    );
                },
            };
            break;
        case Cell.BILL_CREATED_AT:
            selected = {
                id: 'created_at',
                label: t('date'),
                type: FormatType.DateLong,
                filterable: false,
                interval: true,
                defaultSort: true,
            };
            break;
        case Cell.USER_ID:
            selected = {
                id: 'user_id',
                label: t('user'),
                sortable: false,
                type: FormatType.Autocomplete,
                api: getListRidersURL(),
                formatCell: (row) => {
                    const { user } = row;
                    return user ? (
                        <Link color="secondary" component={RouterLink} to={`${paths.riders}/${user.id}`}>
                            {user.name}
                        </Link>
                    ) : (
                        'N/A'
                    );
                },
            };
            break;
        case Cell.COMPANY:
            selected = {
                id: 'company',
                label: t('company'),
                sortable: false,
                filterable: false,
            };
            break;
        case Cell.COMPANY_ID:
            selected = {
                id: 'company_id',
                label: t('company'),
                sortable: false,
                type: FormatType.Autocomplete,
                api: getListCompaniesURL(),
                formatCell: (row) => {
                    let company = row?.company ? (
                        <Link color="secondary" component={RouterLink} to={`${paths.companies}/${row.company.id}`}>
                            {row.company.name}
                        </Link>
                    ) : (
                        'N/A'
                    );
                    if (row.company && row.company.parent) {
                        company = (
                            <>
                                {company}
                                <br />
                                <Link
                                    color="secondary"
                                    component={RouterLink}
                                    to={`${paths.companies}/${row.company.parent.id}`}
                                >
                                    ({row.company.parent.name})
                                </Link>
                            </>
                        );
                    }
                    return company;
                },
            };
            break;
        case Cell.RIDE_NUMBER:
            selected = {
                id: 'ride_number',
                label: t('booking'),
                formatCell: (row) =>
                    row?.ride?.id ? (
                        <Link color="secondary" component={RouterLink} to={`${paths.bookings}/${row.ride.id}`}>
                            N°{row.ride_number}
                        </Link>
                    ) : (
                        'N/A'
                    ),
            };
            break;
        case Cell.PARTNER_ID:
            selected = {
                id: 'partner_id',
                label: t('common:form.partner'),
                sortable: false,
                type: FormatType.Autocomplete,
                api: getListPartnersURL(),
                formatCell: function (row) {
                    const { partner } = row;
                    return partner ? partner.name : 'N/A';

                    // return partner ? (
                    //     <Link color="secondary" component={RouterLink} to={`${paths.riders}/${partner.id}`}>
                    //         {partner.name}
                    //     </Link>
                    // ) : (
                    //     'N/A'
                    // );
                },
            };
            break;
        case Cell.LABEL:
            selected = { id: 'label', label: t('reason') };
            break;
        case Cell.AMOUNT_PRE_TAX:
            selected = { id: 'amount_pre_tax', label: t('amount_without_vat'), type: FormatType.Price };
            break;
        case Cell.AMOUNT_WITH_TAX:
            selected = { id: 'amount_with_tax', label: t('amount_with_vat'), type: FormatType.Price };
            break;

        case Cell.STATUS:
            selected = {
                id: 'status',
                label: t('status'),
                type: FormatType.Picker,
                formatCell: function (row) {
                    return t('partner_bill_status.' + row.status);
                },
            };
            break;
        case Cell.PERIOD:
            selected = {
                id: 'start_date',
                label: t('period'),
                filterable: false,
                formatCell: function (row) {
                    return `${t('common:date_from')} ${DateUtils.tsToDate(row.start_date, true)} ${t(
                        'common:date_to',
                    )} ${DateUtils.tsToDate(row.end_date, true)}`;
                },
            };
            break;
        case Cell.CREATION_DATE:
            selected = {
                id: 'creation_date',
                label: t('creation_date'),
                type: FormatType.DateLong,
                filterable: false,
            };
            break;
        default:
            return null;
    }
    return { ...selected, ...args };
}
